// assertion utility
const assert = ( name, val, type ) => {
  if ( typeof val !== type ) {
    throw new TypeError( `${ name } must be a ${ type }, got ${ val }` );
  }
};

/**
 * EventEmitter Class
 */

class EventEmitter {

  /**
   * EventEmitter constructor
   * @return {EventEmitter}
   */

  constructor() {
    this.off();
  }

  /**
   * Bind an event handler
   * @param  {String}       key     – event name
   * @param  {Function}     handler – event handler
   * @return {EventEmitter}
   */

  on( key, handler ) {
    assert( 'key', key, 'string' );
    assert( 'handler', handler, 'function' );

    const record = this.cache;
    const evts = record.events[ key ] || ( record.events[ key ] = [] );

    evts.push( handler );
    record.count++;

    return this;
  }

  /**
   * Emit an event
   * @param  {String}       key  – event name
   * @param  {...mixed}     args – optional event handler arguments
   * @return {EventEmitter}
   */

  emit( key, ...args ) {
    assert( 'key', key, 'string' );

    const record = this.cache;

    if ( record.events[ key ] ) {
      record.events[ key ].forEach( fn => fn.call( this, ...args ) );
    }

    return this;
  }

  /**
   * Unbind an event
   *
   * Unbind all events:
   * @return {EventEmitter}
   *
   * Unbind all events of a given type:
   * @param  {String}       key – event name
   * @return {EventEmitter}
   *
   * Unbind all events of a given type that match the supplied handler:
   * @param  {String}       key     – event name
   * @param  {Function}     handler – a bound handler function
   * @return {EventEmitter}
   */

  off( key, handler ) {
    if ( arguments.length === 0 ) {
      Object.defineProperty( this, 'cache', {
        value: { events: Object.create( null ), count: 0 },
        writable: false,
        configurable: true,
        enumerable: false
      });
      return this;
    }

    assert( 'key', key, 'string' );

    if ( arguments.length > 1 ) {
      assert( 'handler', handler, 'function' );
    }

    const record = this.cache;
    const evts = record.events[ key ];

    if ( evts && evts.length ) {
      const keep = handler ? evts.filter( fn => fn !== handler ) : [];
      record.events[ key ] = keep;
      record.count -= ( evts.length - keep.length );
    }

    return this;
  }

  /**
   * The number of bound listeners
   * @return {Number}
   */

  get listeners() {
    return this.cache.count;
  }

}

export default EventEmitter;
